import {
    Button, ButtonProps, ButtonVariant, ClickEvent, Panel, PanelProps, Table, TableSelectionEvent
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { Logger, LogManager, ModelRow } from "@mcleod/core";
import { MobileCommGroupFilter } from "./MobileCommGroupFilter";
import { MobileCommGroupList } from "./MobileCommGroupList";

interface ActionButtonProps {
    button: Button;
    shouldEnable?: (table: Table, modelRow: ModelRow) => boolean;
}

const buttonProps: Partial<ButtonProps> = {
    color: "primary", variant: ButtonVariant.round, borderWidth: 0,
    imageWidth: 22, imageHeight: 22, rowBreak: false, enabled: false
}

const borderPanelProps: Partial<PanelProps> = {
    height: 20,
    padding: 0,
    marginRight: 10,
    borderRightWidth: 1,
    borderRightColor: "subtle.light",
    fillRow: true,
    rowBreak: false
}

/**
 * There are 3 steps to adding a new action button:
 * 1. Add a new ActionButtonType enum value.
 * 2. Create a new function that will return the button.
 * 3. Using the new enum value and the function from step 2, create a new ActionButtonProps object and add it to the
 *   buttonProps map in the buildButtonMap() function.  See the comment in ActionButtonProps interface for more information.
 */

export enum ActionButtonType {
    FILTER
}

const log: Logger = LogManager.getLogger("MobileCommGroupListToolPanel");

export class MobileCommGroupListToolPanel extends Panel {
    private table: Table;
    private buttonMap: Map<ActionButtonType, ActionButtonProps>;
    private buttonTypes: ActionButtonType[];

    constructor(layout: MobileCommGroupList, ...buttonsTypes: ActionButtonType[]) {
        super({ marginLeft: 15, paddingTop: 0, paddingBottom: 0 });

        this.table = layout.tableMCGroup;
        this.buttonTypes = buttonsTypes;
        if (buttonsTypes.length > 0) {
            this.buildButtonMap();
            this.addActionButtons();
            this.table.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
        }
        this.table.addCenterTool(this);
    }

    private onSelectionChanged(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data as ModelRow;
        this.buttonTypes.forEach(buttonType => {
            const props = this.buttonMap.get(buttonType);
            if (props.shouldEnable != null) {
                props.button.enabled = props.shouldEnable(this.table, modelRow);
            } else {
                props.button.enabled = true;
            }
        })
    }

    /**
     * When adding a new action button, add it to the buttonProps map here.
     * @private
     */
    private buildButtonMap() {
        this.buttonMap = new Map();

        this.buttonMap.set(ActionButtonType.FILTER, {
            button: this.getFilterButton(), shouldEnable(table, modelRow) {
                return table.selectedRows.length > 0;
            }
        });
    }

    /**
     * This adds the action buttons to the panel.
     */
    async addActionButtons() {
        this.add(new Panel({ ...borderPanelProps, marginLeft: 0, marginRight: 10 }));
        this.buttonTypes.forEach(buttonType => this.add(this.buttonMap.get(buttonType).button));
        this.add(new Panel({ ...borderPanelProps, marginLeft: 10, marginRight: 0 }));
        if (this.components.length == 2) {
            this.removeAll();
        }
    }

    public getButton(buttonType: ActionButtonType): Button {
        return this.buttonMap.get(buttonType).button;
    }

    private getFilterButton(): Button {
        const buttonFilter = new Button({
            ...buttonProps,
            id: "buttonFilter",
            imageName: ImageName.funnel,
            enabled: false,
            tooltip: "Filter"
        });
        buttonFilter.addClickListener((event: ClickEvent) => this.filterOnClick(event));
        return buttonFilter;
    }

    filterOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        const data = selectedRow.data;
        const groupId = data.get("id");
        const options = {
            groupId: groupId,
        };
        MobileCommGroupFilter.showSlider(options);
    }
}
